import { CalendarIcon, CommentDiscussionIcon, SearchIcon, SyncIcon } from '@primer/octicons-react';
import { useRouter } from 'next/router';
import React, { Suspense } from 'react';
import { graphql } from 'react-relay';

import AlimTalkTaskConnectionDataTable from '../../components/alimTalk/AlimTalkTaskConnectionDataTable';
import AlimTalkTaskWindowPaginator from '../../components/alimTalk/AlimTalkTaskWindowPaginator';
import Button from '../../components/core/Button';
import CheckboxGroupField from '../../components/core/CheckboxGroupField';
import EmptyState from '../../components/core/EmptyState';
import ErrorBoundary from '../../components/core/ErrorBoundary';
import Grid from '../../components/core/Grid';
import Head from '../../components/core/Head';
import IconButton from '../../components/core/IconButton';
import { HeaderSidebarNavPageLayout } from '../../components/core/Layout';
import QueryFormik from '../../components/core/QueryFormik';
import Spinner from '../../components/core/Spinner';
import Stack from '../../components/core/Stack';
import Text from '../../components/core/Text';
import Tooltip from '../../components/core/Tooltip';
import View from '../../components/core/View';
import { usePaginationContext } from '../../contexts/PaginationContext';
import useInitialValuesFromParsedUrlQuery from '../../hooks/useInitialValuesFromParsedUrlQuery';
import { alimTalkTask_alimTalkTasksQuery } from '../../relay/__generated__/alimTalkTask_alimTalkTasksQuery.graphql';
import { numberWithCommas } from '../../utils/number';
import { NextPage } from '../_app';
import AlimTalkTaskId from './[alimTalkTaskId]';

const alimTalkTasksForAlimTalkTask = graphql`
  query alimTalkTask_alimTalkTasksQuery(
    $filters: AlimTalkTaskFilter
    $order: AlimTalkTaskOrder
    $page: Int
    $pageSize: Int
  ) {
    ...AlimTalkTaskWindowPaginator_query @arguments(filters: $filters, order: $order, page: $page, pageSize: $pageSize)
  }
`;

type Props = {};

const AlimTalkTask: NextPage<Props> = () => {
  const router = useRouter();
  const { initialValues, setParsedUrlQuery } = useInitialValuesFromParsedUrlQuery({
    search: { type: 'string' },
    isActive_In: { type: 'boolean', multiple: true },
    isRepeat_In: { type: 'boolean', multiple: true },

    page: { type: 'number' },
  });
  const { pageSize } = usePaginationContext();

  const { search, page, isActive_In, isRepeat_In } = initialValues;

  return (
    <View>
      <Head siteTitle={'알림톡'} />
      <ErrorBoundary>
        <QueryFormik<alimTalkTask_alimTalkTasksQuery>
          query={alimTalkTasksForAlimTalkTask}
          staticVariables={{ pageSize }}
          initialValues={{
            filters: {
              search,
              isActive_In,
              isRepeat_In,
            },
            order: '-created',
            page,
          }}
          options={{ fetchPolicy: 'store-and-network' }}
          onSubmit={(values) => setParsedUrlQuery({ ...values.filters }, { scroll: false })}
        >
          {({ values: { filters, order }, resetForm, setFieldValue, submitForm }, queryReference) => {
            const handleReset = () => {
              resetForm({ values: { order } });
              setParsedUrlQuery({}, { scroll: false });
            };

            return (
              <View>
                <Grid sx={{ alignItems: 'center' }} gapX={2}>
                  <Grid.Unit size={'max'}>
                    <Text as={'h1'}>알림톡</Text>
                  </Grid.Unit>
                  <Grid.Unit size={'min'}>
                    <Tooltip text={'룰루의 요술 달력'}>
                      <IconButton
                        size={'large'}
                        aria-label={'룰루의 요술 달력'}
                        variant={'default'}
                        icon={CalendarIcon}
                        onClick={() => {
                          window.open(`${process.env.NEXT_PUBLIC_TCMS_END_POINT}alimtalk/calendar`, '_blank');
                        }}
                      />
                    </Tooltip>
                  </Grid.Unit>
                </Grid>
                <Grid>
                  <Grid.Unit size={'min'}>
                    <QueryFormik.FilterSearchTextField
                      typename={'AlimTalkTaskFilter'}
                      label={'Search'}
                      labelConfig={{ visuallyHidden: true }}
                      name={'filters.search'}
                      size={'large'}
                      autoComplete={'off'}
                      leadingVisual={SearchIcon}
                      debounce
                      onChange={(e) => {
                        setFieldValue('filters.search', e.target.value);
                        setTimeout(() => submitForm(), 0);
                      }}
                    />
                  </Grid.Unit>
                  <Grid.Unit size={'max'}>
                    <View sx={{ display: 'flex', justifyContent: 'end' }}>
                      <Stack gapX={2}>
                        <Stack.Item>
                          <Button leadingIcon={SyncIcon} variant={'plain'} onClick={handleReset}>
                            초기화
                          </Button>
                        </Stack.Item>
                      </Stack>
                    </View>
                  </Grid.Unit>
                </Grid>
                <View sx={{ marginTop: 5 }}>
                  <QueryFormik.FilterGrid
                    onChange={(filters) => {
                      setParsedUrlQuery({ ...filters, order }, { scroll: false });
                    }}
                  >
                    <QueryFormik.FilterGridUnit size={[1, 1, 1 / 2]} title={'반복'}>
                      <CheckboxGroupField
                        name={'filters.isRepeat_In'}
                        label={'반복'}
                        options={[
                          { id: 'true', text: 'O' },
                          { id: 'false', text: 'X' },
                        ]}
                        renderContainer={(children) => (
                          <View sx={{ paddingX: 2 }}>
                            <Grid gapX={2}>{children}</Grid>
                          </View>
                        )}
                        renderOptionWrapper={(children, { id }) => (
                          <Grid.Unit key={id} size={'min'}>
                            {children}
                          </Grid.Unit>
                        )}
                        value={filters?.isRepeat_In?.map((value) => (value === true ? 'true' : 'false'))}
                        onChange={(selected) =>
                          setFieldValue(
                            'filters.isRepeat_In',
                            selected.map((selectedValue) => (selectedValue === 'true' ? true : false)),
                          )
                        }
                      />
                    </QueryFormik.FilterGridUnit>
                    <QueryFormik.FilterGridUnit size={[1, 1, 1 / 2]} title={'활성화 여부'}>
                      <CheckboxGroupField
                        name={'filters.isActive_In'}
                        label={'활성화 여부'}
                        options={[
                          { id: 'true', text: 'O' },
                          { id: 'false', text: 'X' },
                        ]}
                        renderContainer={(children) => (
                          <View sx={{ paddingX: 2 }}>
                            <Grid gapX={2}>{children}</Grid>
                          </View>
                        )}
                        renderOptionWrapper={(children, { id }) => (
                          <Grid.Unit key={id} size={'min'}>
                            {children}
                          </Grid.Unit>
                        )}
                        value={filters?.isActive_In?.map((value) => (value === true ? 'true' : 'false'))}
                        onChange={(selected) =>
                          setFieldValue(
                            'filters.isActive_In',
                            selected.map((selectedValue) => (selectedValue === 'true' ? true : false)),
                          )
                        }
                      />
                    </QueryFormik.FilterGridUnit>
                  </QueryFormik.FilterGrid>
                </View>

                <View sx={{ marginTop: 5 }}>
                  <ErrorBoundary key={queryReference?.fetchKey}>
                    <Suspense fallback={<Spinner />}>
                      <QueryFormik.PreloadedQueryRenderer<alimTalkTask_alimTalkTasksQuery>>
                        {(queryReference) => (
                          <AlimTalkTaskWindowPaginator
                            fragmentReference={queryReference}
                            onLoadPage={(page) => setParsedUrlQuery({ ...filters, order, page })}
                          >
                            {({ alimTalkTasks }, { renderPagination }) => (
                              <>
                                <View>
                                  <Text sx={{ fontSize: 1, fontWeight: 'bold', color: 'fg.muted' }}>
                                    총 {numberWithCommas(alimTalkTasks?.totalCount || 0)}건
                                  </Text>
                                </View>
                                <View sx={{ marginTop: 3 }}>
                                  <AlimTalkTaskConnectionDataTable
                                    alimTalkTaskConnection={alimTalkTasks}
                                    onRowClick={({ id }) => router.push({ pathname: `/alimTalkTask/${id}` })}
                                    emptyState={
                                      <View sx={{ paddingY: 3 }}>
                                        <EmptyState
                                          title={'필터에 맞는 결과가 없어요'}
                                          description={'다른 필터로 다시 시도해보세요.'}
                                        />
                                      </View>
                                    }
                                  />
                                </View>
                                <View sx={{ display: 'flex', justifyContent: 'center', marginTop: 3 }}>
                                  {renderPagination?.()}
                                </View>
                              </>
                            )}
                          </AlimTalkTaskWindowPaginator>
                        )}
                      </QueryFormik.PreloadedQueryRenderer>
                    </Suspense>
                  </ErrorBoundary>
                </View>
              </View>
            );
          }}
        </QueryFormik>
      </ErrorBoundary>
    </View>
  );
};

AlimTalkTask.getLayout = (page) => <HeaderSidebarNavPageLayout>{page}</HeaderSidebarNavPageLayout>;
AlimTalkTask.authenticated = true;
AlimTalkTask.routes = [
  {
    id: 'alimTalkTask',
    icon: CommentDiscussionIcon,
    pathname: '/alimTalkTask',
    name: '알림톡',
    permissions: ['app_push_read'],
  },
  ...AlimTalkTaskId.routes,
];

export default AlimTalkTask;
